import Promise from 'bluebird';
import _ from 'lodash';

import store from '../../store';
import { OVERVIEW_PANEL_GET_CURRENT, OVERVIEW_PANEL_IS_VISIBLE } from '../../store/modules/overview-panel/types';
import filters from '../core/filters';
import worldMap from '../core/map';
import { pinBuilder } from '../core/map/pinBuilder';
import templates from '../core/templates';
import ColorBrewer from '../core/tools/colorbrewer';
import translate from '../core/tools/translate';
import environment from '../environment';
import { notify, NOTIFY_TYPE } from '../shared/notify';
import Leaflet from './Leaflet';
// import 'mapbox.js';

const LayerControls = (name) => require('../controls').get(name);

const get = (name, currentMap = worldMap.getCurrent()) => {
    return require('./index').default.get(name, currentMap);
};

const name = 'Retention';

class RetentionLayer {
    constructor (currentMap) {
        const mapId = +currentMap._id;

        const clusterImage = {
            iconMarker: new Image(),
            OwnRetention: new Image(),
            GroupSpreading: new Image(),
            BrandSpreading: new Image(),
            NoRetention: new Image(),
            GroupRetention: new Image(),
            BrandRetention: new Image()
        };

        clusterImage.iconMarker.src = `${environment.get('IMAGES_HOST')}/icon_marker_retention.png`;
        clusterImage.OwnRetention.src = `${environment.get('IMAGES_HOST')}/icon_circle_lg_blue.png`;
        clusterImage.GroupSpreading.src = `${environment.get('IMAGES_HOST')}/icon_circle_lg_purple.png`;
        clusterImage.BrandSpreading.src = `${environment.get('IMAGES_HOST')}/icon_circle_lg_orange.png`;
        clusterImage.NoRetention.src = `${environment.get('IMAGES_HOST')}/icon_circle_lg_red.png`;
        clusterImage.GroupRetention.src = `${environment.get('IMAGES_HOST')}/icon_circle_lg_purple.png`;
        clusterImage.BrandRetention.src = `${environment.get('IMAGES_HOST')}/icon_circle_lg_green.png`;

        const clusterTemplate = (cluster) => {
            if (LayerControls(name).getControls(mapId).buttonGroup.layerType.type === 'cluster') {
                const data = {
                    percent: [],
                    value: cluster.getChildCount(),
                    colors: []
                };

                let total = 0;
                const categories = {};

                _.forEach(cluster.getAllChildMarkers(), (marker) => {
                    if (marker.feature) {
                        total++;
                        const prop = marker.feature.properties.type;
                        categories[prop] = categories[prop] + 1 || 1;
                    }
                });

                if (total > 0) {
                    _.forEach(get(name, currentMap).options.symbology.values, (values, index) => {
                        data.percent.push((categories[values.value] / total) || 0);
                        data.colors[index] = values.color;
                    });
                } else {
                    delete data.percent;
                }

                return pinBuilder.create.pin.imageWithValue(data, clusterImage.iconMarker);
            } else {
                const types = LayerControls(name).getTypes();

                const color = _(cluster.getAllChildMarkers())
                    .groupBy(m => m.feature.properties.type)
                    .map((val, key) => ({ id: _.find(types, t => t.type === key).id, text: key, value: _.size(val) }))
                    .orderBy('id')
                    .maxBy('value')
                    .text;
                return pinBuilder.create.pin.imageWithValue({
                    value: cluster.getChildCount(),
                    color: '#2196F3'
                }, clusterImage[color]);
            }
        };

        const onSuccess = (res) => {
            if (!res.features.length) {
                notify(NOTIFY_TYPE.LOG, 'Não há dados para a loja selecionada');
            }

            const layerType = LayerControls(name).getControls(mapId).buttonGroup.layerType.type;
            if (layerType === 'decluster') {
                get(name, currentMap).setOptions({ controlRange: 1, type: 'cluster' });
            } else if (layerType === 'cluster' && get(name, currentMap).options.controlRange === 1) {
                get(name, currentMap).setOptions({ controlRange: 30 });
            }

            return res;
        };

        const onComplete = () => {
            get(name, currentMap).setOptions({ type: LayerControls(name).getControls(mapId).buttonGroup.layerType.type });

            if (store.getters[OVERVIEW_PANEL_GET_CURRENT] === name && store.getters[OVERVIEW_PANEL_IS_VISIBLE] && LayerControls(name).getControls(mapId).button.overview.enable) {
                LayerControls(name).toggleOverview();
            }

            if (filters.isVisible()) {
                LayerControls(name).createFilter();
            }
        };

        const getIcon = (t) => {
            const pin = _.find(
                get(name, currentMap).options.symbology.values,
                (v) => v.value === t.type
            ).style;
            return pin ? pin.icon.options.iconUrl : new Image();
        };
        const getText = (t) => translate(`Retention.Classification.${t.type}`);

        return new Leaflet.D3Layer({
            map: currentMap,
            remote: {
                url: `${environment.get('API_HOST')}/lojaconcessionaria/getClients`,
                params: {
                    IdLoja: 0
                }
            },
            // cache: true
            sort: 'type',
            showAll: true,
            autofocus: false,
            controlRange: 30,
            uniqueField: 'id',
            // incremental: true,
            type: 'decluster',
            success: onSuccess,
            reload: onSuccess,
            done: onComplete,
            above: true,
            popup: {
                trigger: 'mouseover',
                template (feature, layer) {
                    return new Promise((resolve, reject) => {
                        const template = $(templates('map-layer-clients-popup', feature));
                        resolve(template);
                    });
                },
                clusterTemplate (features, layer) {
                    if (worldMap.getZoom() < 8) return;
                    const layerType = LayerControls(name).getControls(mapId).buttonGroup.layerType.type;
                    if (layerType === 'decluster') {
                        this.options.offset = L.point(0, -16);
                    } else {
                        this.options.offset = L.point(0, -60);
                    }
                    return new Promise((resolve, reject) => {
                        const types = LayerControls(name).getTypes();
                        const total = _.size(features);

                        const dataGroup = _(features)
                            .groupBy('feature.properties.type')
                            .map((v, k) =>
                                ({
                                    id: _.find(types, t => t.type === k).id,
                                    icon: _.find(get(name, currentMap).options.symbology.values, (s) => s.value === k).style.icon.options.iconUrl,
                                    value: _.size(v),
                                    percent: _.size(v) / total,
                                    text: translate(`Retention.Classification.${k}`).replace(/Atendidos ((de)?(do)? )?/, '').replace(/Retorno ((em)?(no)? )?/, '')
                                })
                            )
                            .value();

                        const data = _(types)
                            .map(t => {
                                if (_.some(dataGroup, d => d.id === t.id)) {
                                    const val = _.find(dataGroup, d => d.id === t.id);
                                    val.color = t.color;
                                    return val;
                                }
                                return ({
                                    text: translate(`Retention.Classification.${t.type}`).replace(/Atendidos ((de)?(do)? )?/, '').replace(/Retorno ((em)?(no)? )?/, ''),
                                    value: 0,
                                    percent: 0,
                                    color: t.color
                                });
                            })
                            .value();

                        const template = $(templates('map-layer-clients-cluster-popup', ({ clients: data })));

                        setTimeout(() => {
                            $('#map-popup-chart').kendoChart({
                                title: {
                                    text: ''
                                },
                                dataSource: { data: _.filter(data, d => parseFloat(d.value) > 0) },
                                seriesColors: _.map(data, d => d.color),
                                seriesDefaults: {
                                    labels: {
                                        position: 'center',
                                        visible: true,
                                        background: 'transparent',
                                        format: '{0}',
                                        font: 'bold 10px "Roboto Condensed", sans-serif',
                                        color: '#FFF'
                                    }
                                },
                                theme: 'bootstrap',
                                chartArea: {
                                    width: 260,
                                    height: 140,
                                    margin: {
                                        top: 0,
                                        bottom: 0,
                                        left: 10,
                                        right: 10
                                    }
                                },
                                legend: {
                                    position: 'right',
                                    labels: {
                                        font: '10px "Roboto Condensed", sans-serif'
                                    }
                                },
                                series: [{
                                    type: 'pie',
                                    field: 'value',
                                    categoryField: 'text'
                                }]
                            });
                        }, 300);

                        resolve(template);
                    });
                },
                options: {
                    offset: L.point(0, -16),
                    closeButton: false,
                    autoclose: true
                },
                single: true
            },
            legend: {
                text: getText,
                icon: getIcon
            },
            symbology: {
                cluster: {
                    disableAtZoom: 20,
                    template: clusterTemplate
                },
                heat: {
                    theme: ColorBrewer.getHeatColors(ColorBrewer.RED_YELLOW_BLUE)
                },
                type: 'uniq',
                property: 'type',
                values: []
            }
        });
    }
}

export default RetentionLayer;



// WEBPACK FOOTER //
// ./src/scripts/layers/RetentionLayer.js